import isNil from 'ramda/src/isNil';
import mergeAll from 'ramda/src/mergeAll';
import * as config from '~web/config.json';
import { log } from '~common/service/logger';

enum BuildType {
  DEBUG = 'debug',
  RELEASE = 'release',
}

enum BuildEnvironment {
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
  DEMO = 'demo',
}

interface EnvironmentVariables {
  dropboxAccessToken: string;
  version: string;
}

// TODO(DMITRY): Move time values to `utils.ts`
type FrontendConfig = Readonly<{
  isDebugBuild: boolean;
  isReleaseBuild: boolean;
  enableTestPaths: boolean;
  environment: BuildEnvironment;
  api: string;
  domain: string;
  gaTrackerId: string;
  googleMapApiKey: string;
  citconCallbackUrl: string;
  paypalBaseUrl: string;
  paypalToken: string;
  paypalReturnUrl: string;
  seatmapBaseUrl: string;
  chromecastApplicationId: string;
  drmMerchantId: string;
  drmLicense: string;
  drmEnv: string;
  version: string;
  time: {
    refreshRate: {
      chance: number | null;
      userProfile: number | null;
    };
    searchThrottle: number;
    timeout: {
      success: number; // timeout to take an action after another successful action
      reset: number;
    };
  };
  formatDurationThreshold: {
    formatAsSeconds: number;
    formatAsMinutes: number;
    formatAsHours: number;
  };
  dateTimeFormats: {
    dateOfBirth: string;
    server: string;
  };
  spreedlyBaseUrl: string;
  spreedlyKey: string;
  videoConferencingSignallingClient: {
    url: string;
    apiKey: string;
    supportedVersion: string;
    tenantRootNetworkId: number;
  };
  firebase: {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
    remoteConfig?: {
      fetchIntervalMillis: number;
      defaults: { [key: string]: string | number | boolean };
    };
  };
  kountScriptUrl: string;
  youbora: {
    accountCode: string;
    appName: string;
  };
  gtm: {
    key: string;
    auth: string;
    preview: string;
  };
}> &
  Readonly<EnvironmentVariables>;

/**
 * The `process.env.NODE_ENV` variable is a special variable "injected" by Webpack
 * at runtime. The word "injected" is in double quotes for a good reason. Indeed,
 * the variable is not really injected, it's really a search & replace at build time
 * that is performed by Webpack. Meaning on the final bundle, this code will look
 * like `return "production"` (assuming `process.env.NODE_ENV` equals `production`).
 */
const getBuildEnvironment = (): string | undefined => process.env.NODE_ENV;

const getBuildType = (): BuildType => {
  const buildEnvironment = getBuildEnvironment();

  if (isNil(buildEnvironment) || buildEnvironment === BuildEnvironment.DEVELOPMENT) {
    return BuildType.DEBUG;
  }

  return BuildType.RELEASE;
};

/**
 * The `process.env.DEPLOYMENT_ENVIRONMENT` variable is a special variable "injected" by Webpack
 * at runtime. The word "injected" is in double quotes for a good reason. Indeed,
 * the variable is not really injected, it's really a search & replace at build time
 * that is performed by Webpack. Meaning on the final bundle, this code will look
 * like `return "production"` (assuming `process.env.DEPLOYMENT_ENVIRONMENT` equals
 * `production`).
 */
const getDeploymentEnvironment = (): BuildEnvironment | undefined =>
  process.env.DEPLOYMENT_ENVIRONMENT as BuildEnvironment;

const getEnvironmentVariables = (): EnvironmentVariables => {
  const dropboxAccessToken = process.env.DROPBOX_ACCESS_TOKEN;
  if (dropboxAccessToken) {
    log.debug(`Dropbox access token: ${dropboxAccessToken}`);
  } else {
    log.debug('Dropbox access token is not set');
  }

  return {
    dropboxAccessToken,
    version: process.env.VERSION,
  };
};

const computeConfig = (environment: BuildEnvironment): FrontendConfig => {
  const buildType = getBuildType();
  const commonConfig: Pick<FrontendConfig, 'isDebugBuild' | 'isReleaseBuild'> = {
    isDebugBuild: buildType === BuildType.DEBUG,
    isReleaseBuild: buildType === BuildType.RELEASE,
  };
  const baseConfig: Partial<FrontendConfig> = config.base;
  const environmentConfig: Partial<FrontendConfig> = config[environment] || {};
  const environmentVariables: EnvironmentVariables = getEnvironmentVariables();

  if (commonConfig.isDebugBuild) {
    log.debug(`You're running a DEBUG build`);
  }
  log.info(`ENVIRONMENT ${environment}`);
  log.info(`VERSION ${environmentVariables.version}`);

  return mergeAll([
    commonConfig,
    baseConfig,
    environmentConfig,
    environmentVariables,
    { environment },
  ]) as FrontendConfig;
};

export const Config: FrontendConfig = computeConfig(getDeploymentEnvironment());



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/config.ts